/**
 * Formatting helpers. Everything the user sees is rendered in the *workspace*
 * timezone, not the browser's — schedules are meaningless otherwise.
 *
 * Timezone conversion uses Intl (no extra dependency); date-fns handles relative
 * time and calendar math.
 */

import { format as formatDate, formatDistanceToNowStrict } from 'date-fns';
import type { PlatformPostStatus, PostStatus } from '../../shared/types';
import { formatBytes, platformPostStatusLabel, postStatusLabel } from '../../shared/utils';

export { formatBytes, platformPostStatusLabel, postStatusLabel };

type Timestamp = number | string | Date | null | undefined;

function toDate(value: Timestamp): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function intl(timeZone: string | undefined, options: Intl.DateTimeFormatOptions) {
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat('en-US', options);
  }
}

/**
 * "YYYY-MM-DD" for an instant *as seen in `timeZone`*.
 *
 * "Today" and "this year" have to be decided in the workspace's zone, not the
 * browser's: a post scheduled for 9pm Chicago is tomorrow to a viewer in
 * Berlin, and labelling it "Today" next to a Chicago clock time would be a lie.
 */
function zonedDayKey(date: Date, timeZone?: string): string {
  const parts = intl(timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function isSameZonedDay(date: Date, timeZone?: string, reference = new Date()): boolean {
  return zonedDayKey(date, timeZone) === zonedDayKey(reference, timeZone);
}

function isSameZonedYear(date: Date, timeZone?: string, reference = new Date()): boolean {
  return zonedDayKey(date, timeZone).slice(0, 4) === zonedDayKey(reference, timeZone).slice(0, 4);
}

/** "Mar 4, 2:30 PM" in the workspace timezone. */
export function formatDateTime(value: Timestamp, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';
  return intl(timeZone, {
    month: 'short',
    day: 'numeric',
    year: isSameZonedYear(date, timeZone) ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

/** "Mar 4, 2026" */
export function formatDay(value: Timestamp, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';
  return intl(timeZone, {
    month: 'short',
    day: 'numeric',
    year: isSameZonedYear(date, timeZone) ? undefined : 'numeric',
  }).format(date);
}

/** "2:30 PM" */
export function formatTime(value: Timestamp, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';
  return intl(timeZone, { hour: 'numeric', minute: '2-digit' }).format(date);
}

/** "Today at 2:30 PM" / "Mar 4 at 2:30 PM" */
export function formatWhen(value: Timestamp, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '—';
  const time = formatTime(date, timeZone);
  return isSameZonedDay(date, timeZone)
    ? `Today at ${time}`
    : `${formatDay(date, timeZone)} at ${time}`;
}

/** "3 minutes ago" / "in 2 hours" */
export function formatRelative(value: Timestamp): string {
  const date = toDate(value);
  if (!date) return '—';
  const distance = formatDistanceToNowStrict(date);
  return date.getTime() > Date.now() ? `in ${distance}` : `${distance} ago`;
}

/** Value for an `<input type="datetime-local">` in the workspace timezone. */
export function toLocalInputValue(value: Timestamp, timeZone?: string): string {
  const date = toDate(value);
  if (!date) return '';
  const parts = intl(timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Offset (minutes) of a timezone at a given instant. */
function timeZoneOffsetMinutes(timeZone: string, at: Date): number {
  const parts = intl(timeZone, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  const asUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return (asUtc - at.getTime()) / 60_000;
}

/**
 * Inverse of `toLocalInputValue`: interpret "2026-03-04T14:30" as wall-clock time
 * in `timeZone` and return the ISO instant to send to the API.
 */
export function fromLocalInputValue(value: string, timeZone?: string): string | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match.map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const naiveUtc = Date.UTC(year, month - 1, day, hour, minute);
  if (!timeZone) return new Date(naiveUtc).toISOString();
  // Two passes handle the DST boundary correctly.
  let guess = new Date(naiveUtc - timeZoneOffsetMinutes(timeZone, new Date(naiveUtc)) * 60_000);
  guess = new Date(naiveUtc - timeZoneOffsetMinutes(timeZone, guess) * 60_000);
  return guess.toISOString();
}

/** date-fns passthrough for calendar grids that need a specific pattern. */
export function formatPattern(value: Timestamp, pattern: string): string {
  const date = toDate(value);
  return date ? formatDate(date, pattern) : '—';
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

/** Tailwind classes for a status chip, keyed by status. */
export const POST_STATUS_TONE: Record<PostStatus, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  draft: 'neutral',
  scheduled: 'info',
  publishing: 'info',
  published: 'success',
  partial: 'warning',
  failed: 'danger',
  archived: 'neutral',
};

export const PLATFORM_POST_STATUS_TONE: Record<
  PlatformPostStatus,
  'neutral' | 'info' | 'success' | 'warning' | 'danger'
> = {
  draft: 'neutral',
  approved: 'info',
  scheduled: 'info',
  publishing: 'info',
  published: 'success',
  failed: 'danger',
  skipped: 'neutral',
};
